import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { ErrorBoundary } from '@sainsburys-tech/bolt'
import { canUseDom } from '@sainsburys-tech/boltui-utils'
import loadable from '@loadable/component'

import Alert from '../../components/Alert/Alert'
import AttributeSelector from '../../components/AttributeSelector/AttributeSelector'
import FulfilmentBoxContainer from '../../components/FulfilmentBox/FulfilmentBoxContainer'
import GenericFlag from '../../components/GenericFlag/GenericFlag'
import WishList from '../../components/WishList/WishList'
import OrderSwatch from '../../components/OrderSwatch/OrderSwatch'
import OrderSwatchDrawer from '../../components/MadeToOrderComponents/OrderSwatchDrawer/OrderSwatchDrawer'
import CreditPlanLink from '../../components/CreditPlanLink/CreditPlanLink'
import AddToTrolleySection from './AddToTrolleySection'
import PromotionContainer from '../../components/Promotion/PromotionContainer'
import ArgosFulfilment from '../../components/ArgosFulfilment/ArgosFulfilment'
import ProductCareComponent from '../../components/ProductCare/ProductCare'
import EssentialExtras from '../../components/EssentialExtras/EssentialExtras'
import PayPalContainer from '../../components/Paypal/PayPalContainer'
import KlarnaMessaging from '../../components/Klarna/KlarnaMessaging'
import FinanceExampleTextBox from '../../components/AFSCredit/components/FinanceExampleTextBox/FinanceExampleTextBox'
import FinanceAccordion from '../../components/AFSCredit/components/FinanceAccordion/FinanceAccordion'
import FinanceProviderCustomWidget from '../../components/AFSCredit/components/FinanceProviderCustomWidget/FinanceProviderCustomWidget'
import PriceContainer from '../../components/PriceContainer/PriceContainer'
import { getPreferredCreditPlans } from '../../helpers/creditPlanHelper'
import { ARGOS_CARD_PROVIDED, PAYPAL_PROVIDED, KLARNA_PROVIDED } from '../../helpers/FinanceProviderConstants'

import { getEssentialExtrasData } from '../../helpers/essentialExtrasHelper'
import { getTradeInConfig } from '../../helpers/tradeInHelper'
import Proposition from '../../components/MadeToOrderComponents/Proposition/Proposition'
import Box from '@/components/Box/Box'
import TradeIn from '@/components/TradeIn/TradeIn'
import PriceGuaranteeMessage from '@/components/PriceGuarantee/PriceGuarantee'
import { isAppExclusiveSKU } from '@/javascript/helpers/appExclusiveSkusHelper'

const GoInStoreWidget = loadable(() =>
  import(/* webpackChunkName: "go-in-store-widget" */ '../../components/GoInStoreWidget/GoInStoreWidget')
)

class DefaultPricingModuleSection extends Component {
  render() {
    const {
      className,
      prices,
      creditPlans,
      genericFlagOptions,
      attributeSelectorOptions,
      fulfilmentBoxOptions,
      productCareOptions,
      addToTrolleyButtonOptions,
      addToTrolleyStickyOptions,
      addToTrolleyModalOptions,
      wishListOptions,
      ArgosCardOptions,
      messageList,
      promotionOptions,
      matchingAccessoriesOptions,
      WISHLIST,
      SOLD_BY_ARGOS,
      FREQUENTY_BROUHT_TOGETHER,
      GO_IN_STORE,
      NECTAR,
      breadcrumb,
      ESSENTIAL_EXTRAS,
      PAYPAL_IFC_MESSAGING,
      PAYPAL_MESSAGING,
      brand,
      partNumber,
      propositions = [],
      showAttributeSelector = true,
      KLARNA_CREDITPLAN,
      KLARNA_MESSAGING,
      digitalDownload,
      isMadeToOrder,
      headingSection,
      PDP_V2_FLAG,
      FULFILMENT_COMPONENT_V2,
      trevorSummarySection,
      badges = []
    } = this.props

    const { showAddToTrolley } = addToTrolleyButtonOptions

    const trolleyOptions = {
      addToTrolleyStickyOptions,
      addToTrolleyModalOptions,
      addToTrolleyButtonOptions
    }
    const { attributes: priceAttributes = {} } = prices || {}

    const { qualifyingCreditPlans, availableProviders } = getPreferredCreditPlans(creditPlans)

    const orderSwatchProps = {
      orderMessage: 'Free fabric sample',
      SKU: attributeSelectorOptions.partNumber,
      showSwatch: attributeSelectorOptions.displaySwatchMessage
    }

    const { warranties = [] } = productCareOptions

    const { essentialExtras, offers = [] } = genericFlagOptions

    const extrasData = getEssentialExtrasData(essentialExtras, matchingAccessoriesOptions)
    const offerBundles = offers?.length && offers.filter(offer => offer.attributes.specialOfferType.includes('bundle'))
    const allOffersAreOfferBundles = JSON.stringify(offers) === JSON.stringify(offerBundles)

    const hasPriceGuaranteeBadge = badges.some(badge => badge.attributes?.badgeReference === 'price_guarantee')
    const BRAND = process.env.BRAND && process.env.BRAND.toUpperCase()
    const priceGuaranteeLink =
      BRAND === 'HABITAT'
        ? 'https://www.habitat.co.uk/help/terms-and-conditions/'
        : 'https://help.argos.co.uk/help/terms-and-conditions/black-friday-price-guarantee'

    const goInStoreEnabled = GO_IN_STORE && canUseDom() // GoInStore widget
    const isEssentialExtraEnabled = ESSENTIAL_EXTRAS && extrasData && showAddToTrolley

    const isAppExclusiveProduct = isAppExclusiveSKU(partNumber)

    const PriceComponent = prices ? (
      <PriceContainer
        priceAttributes={priceAttributes}
        creditPlans={creditPlans}
        ShowPaypalCreditOption={!digitalDownload}
        PAYPAL_IFC_MESSAGING={PAYPAL_IFC_MESSAGING}
        removeMargin={isAppExclusiveProduct}
      />
    ) : (
      <Alert {...{ messageList }} />
    )
    const AddToTrolleyComponent = <AddToTrolleySection {...trolleyOptions} />
    const WishListComponent = WISHLIST ? (
      <ErrorBoundary>
        <WishList {...wishListOptions} />
      </ErrorBoundary>
    ) : null
    const ArgosFulfilmentComponent = SOLD_BY_ARGOS && <ArgosFulfilment />

    const matchingCategory = getTradeInConfig(breadcrumb, brand, partNumber)

    const CreditAndOffersComponent = isAppExclusiveProduct ? null : (
      <>
        {PAYPAL_IFC_MESSAGING &&
          !digitalDownload &&
          qualifyingCreditPlans.length > 0 &&
          availableProviders.includes(PAYPAL_PROVIDED) && (
            <ErrorBoundary>
              <FinanceExampleTextBox />
            </ErrorBoundary>
          )}
        {availableProviders.includes(ARGOS_CARD_PROVIDED) && (
          <ErrorBoundary>
            <CreditPlanLink {...ArgosCardOptions} />
          </ErrorBoundary>
        )}

        {!digitalDownload && (
          <FinanceAccordion
            title='Other flexible credit'
            showThirdPartyPayments={!digitalDownload}
            PAYPAL_IFC_MESSAGING={PAYPAL_IFC_MESSAGING}
            KLARNA_MESSAGING={KLARNA_MESSAGING}
            PAYPAL_MESSAGING={PAYPAL_MESSAGING}
            KLARNA_CREDITPLAN={KLARNA_CREDITPLAN}
            availableProviders={availableProviders}
          >
            {availableProviders.map(provider => {
              if (provider === KLARNA_PROVIDED && KLARNA_CREDITPLAN && KLARNA_MESSAGING && !digitalDownload) {
                return (
                  <ErrorBoundary key='klarna'>
                    <KlarnaMessaging price={priceAttributes.now} dcid={KLARNA_MESSAGING.dcid} />
                  </ErrorBoundary>
                )
              }

              if (
                provider === PAYPAL_PROVIDED &&
                PAYPAL_IFC_MESSAGING &&
                !digitalDownload &&
                qualifyingCreditPlans.length > 0
              ) {
                return (
                  <ErrorBoundary key='paypal'>
                    <FinanceProviderCustomWidget creditPlans={creditPlans} />
                  </ErrorBoundary>
                )
              }

              return null
            })}

            {!KLARNA_CREDITPLAN && KLARNA_MESSAGING && !digitalDownload && (
              <ErrorBoundary>
                <KlarnaMessaging price={priceAttributes.now} dcid={KLARNA_MESSAGING.dcid} />
              </ErrorBoundary>
            )}

            {PAYPAL_MESSAGING && !digitalDownload && (
              <ErrorBoundary>
                <PayPalContainer price={priceAttributes.now} />
              </ErrorBoundary>
            )}
          </FinanceAccordion>
        )}

        {NECTAR && (
          <ErrorBoundary>
            <PromotionContainer {...promotionOptions} />
          </ErrorBoundary>
        )}
        <ErrorBoundary>
          {offerBundles?.length > 0 && (
            <GenericFlag type='moneyOffBundles' {...genericFlagOptions} offerBundles={offerBundles} />
          )}
          {!allOffersAreOfferBundles && <GenericFlag type='offers' {...genericFlagOptions} />}
        </ErrorBoundary>
        <ErrorBoundary>
          {FREQUENTY_BROUHT_TOGETHER && <GenericFlag type='bundles' {...genericFlagOptions} />}
        </ErrorBoundary>
        {hasPriceGuaranteeBadge && (
          <ErrorBoundary>
            <PriceGuaranteeMessage link={priceGuaranteeLink} brand={BRAND} />
          </ErrorBoundary>
        )}
        {matchingCategory && (
          <ErrorBoundary>
            <TradeIn heading={matchingCategory.heading} subtext={matchingCategory.subtext} />
          </ErrorBoundary>
        )}
      </>
    )

    const hasAttributeBox = showAttributeSelector && !!attributeSelectorOptions?.variants?.length

    const SwatchComponent = isMadeToOrder ? (
      <ErrorBoundary>
        <OrderSwatchDrawer showSwatch={attributeSelectorOptions.displaySwatchMessage} />
      </ErrorBoundary>
    ) : orderSwatchProps?.showSwatch ? (
      <ErrorBoundary>
        <OrderSwatch {...orderSwatchProps} />
      </ErrorBoundary>
    ) : null

    const isProductCareAvailable = warranties.length > 0

    return (
      <section className={className}>
        <Box removeMobileTopBorders>
          {headingSection}
          <ErrorBoundary>{PriceComponent}</ErrorBoundary>
          <ErrorBoundary>{CreditAndOffersComponent}</ErrorBoundary>
          {!hasAttributeBox && SwatchComponent}
        </Box>

        {hasAttributeBox && (
          <Box>
            {!PDP_V2_FLAG && SwatchComponent}
            <ErrorBoundary>
              <AttributeSelector {...attributeSelectorOptions} />
            </ErrorBoundary>
            {PDP_V2_FLAG && SwatchComponent}
          </Box>
        )}

        <Box>
          <ErrorBoundary retries={2}>
            <FulfilmentBoxContainer {...fulfilmentBoxOptions} isV2={FULFILMENT_COMPONENT_V2} />
          </ErrorBoundary>
          {AddToTrolleyComponent}
          {ArgosFulfilmentComponent}
          {WishListComponent}
        </Box>

        <Box>
          {propositions?.length > 0 && (
            <div>
              {propositions.map(proposition => (
                <Proposition key={proposition.heading} {...proposition} />
              ))}
            </div>
          )}
          {isProductCareAvailable && (
            <ErrorBoundary>
              <ProductCareComponent {...productCareOptions} />
            </ErrorBoundary>
          )}
          {isEssentialExtraEnabled && (
            <ErrorBoundary>
              <EssentialExtras {...extrasData} noProductCare={!isProductCareAvailable && PDP_V2_FLAG} />
            </ErrorBoundary>
          )}
        </Box>

        {goInStoreEnabled && (
          <GoInStoreWidget
            productBrand={brand}
            productCategory={breadcrumb && breadcrumb.length > 0 ? breadcrumb[breadcrumb?.length - 1]?.name : ''}
          />
        )}

        <Box>{trevorSummarySection}</Box>
      </section>
    )
  }
}

DefaultPricingModuleSection.propTypes = {
  className: PropTypes.string.isRequired,
  prices: PropTypes.objectOf(PropTypes.object).isRequired,
  genericFlagOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  attributeSelectorOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  fulfilmentBoxOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  productCareOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  addToTrolleyButtonOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  addToTrolleyStickyOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  addToTrolleyModalOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  wishListOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  creditMessageAccordionOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  messageList: PropTypes.arrayOf(PropTypes.string).isRequired,
  promotionOptions: PropTypes.objectOf(PropTypes.object).isRequired,
  WISHLIST: PropTypes.bool.isRequired,
  FEATURED_PRODUCTS: PropTypes.bool.isRequired,
  showAttributeSelector: PropTypes.bool.isRequired,
  headingSection: PropTypes.element.isRequired,
  trevorSummarySection: PropTypes.element.isRequired
}

export default DefaultPricingModuleSection
